interface PriceData {
  price: number;
  cached?: boolean;
  fallback?: boolean;
  error?: string;
}

class PriceService {
  private static instance: PriceService;
  private cachedPrice: number | null = null;
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 60000; // 1 minute
  private readonly FALLBACK_PRICE = 186; // Fallback price in case API fails
  private readonly API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  private constructor() {}

  static getInstance(): PriceService {
    if (!PriceService.instance) {
      PriceService.instance = new PriceService();
    }
    return PriceService.instance;
  }

  async getSOLPrice(): Promise<number> {
    const now = Date.now();
    
    // Return cached price if it's still fresh
    if (this.cachedPrice && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.cachedPrice;
    }

    try {
      // Use backend proxy to avoid CORS issues
      const response = await fetch(`${this.API_URL}/api/price/sol`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: PriceData = await response.json();
      
      if (data.price) {
        this.cachedPrice = data.price;
        this.lastFetchTime = now;
        return this.cachedPrice;
      } else {
        throw new Error('Invalid price data received');
      }
    } catch (error) {
      console.warn('Failed to fetch SOL price, using fallback:', error);
      
      // Return cached price if available, otherwise use fallback
      return this.cachedPrice || this.FALLBACK_PRICE;
    }
  }

  // Method to clear cache and force fresh fetch
  invalidateCache(): void {
    this.cachedPrice = null;
    this.lastFetchTime = 0;
  }

  // Method to get cached price without fetching (useful for synchronous operations)
  getCachedPrice(): number {
    return this.cachedPrice || this.FALLBACK_PRICE;
  }
}

export const priceService = PriceService.getInstance();